'use es6';

import I18n from 'I18n';
import { DATAWELL } from 'reporting-visualizations/constants/visualizationTypes';
import { DatawellVisualization, Directions } from 'reporting-visualizations/visualizations/datawell/datawellRecords';
import { getReportGroupEncoding, getReportValuesEncodings } from '../../utils/report-utils';
import { getEncodingColumnAlias } from '../../utils/visual-utils';
import { dateComparisonFilterToState, reportHasDateComparisonFilter } from '../../utils/date-comparison-filter-utils';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { Map as ImmutableMap, List } from 'immutable';
import { selectAndSortFieldValues } from 'reporting-visualizations/field/sort';
import { Dataset } from 'reporting-data/v2/dataset/datasetTypes';
import { getDatePartTransformValue, isDateLikeField } from '../../utils/column-utils';
import { SortOrders } from '../../schema/sort-records';
import { DATE_PART_MOMENT_ACCESSORS } from '../../resolve/format';
import { DatePartTypes } from '../../schema/column-records';
import { getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
const applyGroupOrder = (dataset, groupField, order) => {
  if (!order) {
    return dataset.data;
  }
  const definedOrder = selectAndSortFieldValues(dataset, groupField, order);
  return dataset.data.sortBy(row => {
    const groupValue = row.get(groupField);
    return definedOrder.indexOf(groupValue);
  });
};
export const getDatePartValueWithOffset = (moment, timeUnit, useFiscalYear = false, fiscalYearMonthName = 'JANUARY') => {
  const datePartMomentAccessor = DATE_PART_MOMENT_ACCESSORS[timeUnit];
  if (useFiscalYear && datePartMomentAccessor === 'quarter') {
    const offset = I18n.moment().month(fiscalYearMonthName).month();
    moment.subtract(offset, 'months');
  }
  const momentValue = moment.get(datePartMomentAccessor);

  // In moment and JS, month parts are 0-indexed, while we need 1-indexed months to match the BE
  if (datePartMomentAccessor === 'month') {
    return momentValue + 1;
  }
  return momentValue;
};
const shouldReverseCompareAndPrimaryDataset = (report, fiscalYearMonthName) => {
  const group = getReportGroupEncoding(report);
  const isDateComparison = reportHasDateComparisonFilter(report);
  if (!isDateComparison) {
    return false;
  }
  const [{
    filter: firstDateComparisonFilter
  }, {
    filter: secondDateComparisonFilter
  }] = report.dateComparisonFilter.filters;
  const firstFilterTimeUnit = firstDateComparisonFilter.get('operation').get('timeUnit');
  const firstFilterOperator = firstDateComparisonFilter.get('operation').get('operator');
  const secondFilterOperator = secondDateComparisonFilter.get('operation').get('operator');
  const useFiscalYear = firstDateComparisonFilter.get('operation').get('useFiscalYear');
  const thisVsLastComparison = firstFilterOperator === 'IN_THIS_TIME_UNIT' && secondFilterOperator === 'IN_LAST_TIME_UNIT';
  if (!thisVsLastComparison) {
    return false;
  }

  // Year never wraps around, therefore never needs to be reversed.
  if (firstFilterTimeUnit === 'YEAR') {
    return false;
  }
  if (isDateLikeField(group.column.field)) {
    return false;
  }
  const nowDatePartValue = getDatePartValueWithOffset(I18n.moment(), firstFilterTimeUnit, useFiscalYear, fiscalYearMonthName);
  const zeroIndexed = firstFilterTimeUnit === 'DAYOFWEEK';
  return zeroIndexed ? nowDatePartValue === 0 : nowDatePartValue === 1;
};
const buildComparisonDatasetWithCompareByChannel = (report, dataset, groupColumn, valueColumn, compareColumn, order, fiscalYearMonthName) => {
  const sortedPrimaryDataset = applyGroupOrder(dataset, groupColumn, order);
  const {
    timeUnit
  } = dateComparisonFilterToState(report.dateComparisonFilter);
  const nowDatePartValue = getDatePartValueWithOffset(I18n.moment(), timeUnit);
  const setOfGroupColumnValues = sortedPrimaryDataset.map(data => data.get(groupColumn)).toSet();
  const setOfCompareColumnValues = sortedPrimaryDataset.map(data => data.get(compareColumn)).toSet().add(nowDatePartValue);
  const maxCompareValue = setOfCompareColumnValues.max();
  const minCompareValue = setOfCompareColumnValues.min();
  const primaryCompare = maxCompareValue;
  const secondaryCompare = minCompareValue;
  const indexedDataset = sortedPrimaryDataset.reduce((nextIndexedDataset, data) => {
    const groupValue = data.get(groupColumn);
    const compareValue = data.get(compareColumn);
    if (nextIndexedDataset.has(groupValue)) {
      return nextIndexedDataset.update(groupValue, compareColumnValues => compareColumnValues.set(compareValue, data));
    } else {
      return nextIndexedDataset.set(groupValue, ImmutableMap().set(compareValue, data));
    }
  }, ImmutableMap());
  const primaryAndCompareDatasetData = setOfGroupColumnValues.reduce((nextPrimaryAndCompareDatasetData, groupValue) => {
    const primaryData = indexedDataset.hasIn([groupValue, primaryCompare]) ? indexedDataset.get(groupValue).get(primaryCompare) : {
      [groupColumn]: groupValue,
      [compareColumn]: primaryCompare,
      [valueColumn]: 0
    };
    const compareData = indexedDataset.hasIn([groupValue, secondaryCompare]) ? indexedDataset.get(groupValue).get(secondaryCompare) : {
      [groupColumn]: groupValue,
      [compareColumn]: secondaryCompare,
      [valueColumn]: 0
    };
    return nextPrimaryAndCompareDatasetData.update('primary', primaryDataset => primaryDataset.push(ImmutableMap(primaryData))).update('compare', compareDataset => compareDataset.push(ImmutableMap(compareData)));
  }, ImmutableMap({
    primary: List(),
    compare: List()
  }));
  const primaryDatasetData = primaryAndCompareDatasetData.get('primary');
  const compareDatasetData = primaryAndCompareDatasetData.get('compare');
  const primaryDataset = Dataset({
    data: primaryDatasetData,
    paginationSize: primaryDatasetData.length,
    properties: dataset.properties
  });
  const compareDataset = Dataset({
    data: compareDatasetData,
    paginationSize: compareDatasetData.length,
    properties: dataset.properties
  });
  const isReversed = shouldReverseCompareAndPrimaryDataset(report, fiscalYearMonthName);
  if (isReversed) {
    return {
      primary: compareDataset,
      compare: primaryDataset
    };
  }
  return {
    primary: primaryDataset,
    compare: compareDataset
  };
};
const buildCompareLeftComparisonDatasetWithGroupBy = (report, primaryDataset, groupColumn, valueColumn, order) => {
  const sortedPrimaryDataset = applyGroupOrder(primaryDataset, groupColumn, order);
  if (sortedPrimaryDataset.size === 1) {
    return {
      compare: Dataset({
        data: primaryDataset.data.map(data => ImmutableMap({
          [groupColumn]: data.get(groupColumn),
          [valueColumn]: 0
        })),
        properties: primaryDataset.properties
      }),
      primary: primaryDataset
    };
  }
  const comparisonDataset = sortedPrimaryDataset.map((data, idx) => {
    if (idx === 0) {
      return ImmutableMap({
        [groupColumn]: data.get(groupColumn),
        [valueColumn]: 0
      });
    } else {
      return ImmutableMap({
        [groupColumn]: data.get(groupColumn),
        [valueColumn]: sortedPrimaryDataset.getIn([idx - 1, valueColumn])
      });
    }
  });
  return {
    compare: Dataset({
      data: comparisonDataset,
      properties: primaryDataset.properties
    }),
    primary: primaryDataset.set('data', sortedPrimaryDataset)
  };
};
const buildCompareLeftComparisonDataset = (primaryDataset, valueEncodings) => {
  const datasetRecord = primaryDataset.data.get(0);
  const comparisonDataset = valueEncodings.reduce((nextDatasetRecord, encoding, idx) => nextDatasetRecord.set(encoding.column, idx === 0 || !datasetRecord ? 0 : datasetRecord.get(valueEncodings.get(idx - 1).column)), ImmutableMap());
  return {
    primary: primaryDataset,
    compare: Dataset({
      data: List([comparisonDataset]),
      properties: primaryDataset.properties
    })
  };
};
const buildComparisonDataset = (report, encodings, primaryDataset, order, fiscalYearMonthName) => {
  const groupColumn = encodings.getIn(['group', 'column']);
  const compareColumn = encodings.getIn(['compare', 'column']);
  const {
    values: valueEncodings
  } = encodings;
  const valueColumn = encodings.getIn(['values', 0, 'column']);
  if (compareColumn && valueColumn && groupColumn) {
    return buildComparisonDatasetWithCompareByChannel(report, primaryDataset, groupColumn, valueColumn, compareColumn, order, fiscalYearMonthName);
  }
  if (valueColumn && groupColumn) {
    return buildCompareLeftComparisonDatasetWithGroupBy(report, primaryDataset, groupColumn, valueColumn, order);
  }
  if (!groupColumn && valueEncodings.length > 1) {
    return buildCompareLeftComparisonDataset(primaryDataset, valueEncodings);
  }
  return {
    primary: primaryDataset
  };
};
export const mapTextToVisual = (report, datasets, __options, {
  fiscalYearMonthName,
  compact
}) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const group = getReportGroupEncoding(report);
  const values = getReportValuesEncodings(report);
  const groupEncodingOptions = group && group.options;
  const valueOptionsByColumn = values.map(v => {
    return {
      column: v.column.get('alias'),
      options: v.options
    };
  });
  const visualOptions = report.getIn(['visual', 'options']);
  const showPercentChangeComparison = visualOptions.get('showPercentChangeComparison', false);
  const mapOrder = () => {
    if (!group || !group.column) {
      return undefined;
    }
    const visualOrder = toVisualizationOrder(group.column, primaryDataset);
    if (showPercentChangeComparison && reportHasDateComparisonFilter(report)) {
      const [{
        filter: firstDateComparisonFilter
      }, {
        filter: secondDateComparisonFilter
      }] = report.dateComparisonFilter.filters;
      const firstFilterTimeUnit = firstDateComparisonFilter.get('operation').get('timeUnit');
      const firstFilterOperator = firstDateComparisonFilter.get('operation').get('operator');
      const secondFilterOperator = secondDateComparisonFilter.get('operation').get('operator');
      const groupColumnDatePartUnit = getDatePartTransformValue(group.column);
      if (firstFilterTimeUnit !== 'YEAR' && groupColumnDatePartUnit === firstFilterTimeUnit) {
        const nowDatePartValue = I18n.moment().get(DATE_PART_MOMENT_ACCESSORS[groupColumnDatePartUnit]);
        const datePartsWithOffset = [DatePartTypes.MONTH, DatePartTypes.DAYOFWEEK];
        const timeUnitOffset = datePartsWithOffset.includes(firstFilterTimeUnit) ? 1 : 0;
        const isFirstTimeUnitComparedToLast = nowDatePartValue + timeUnitOffset === 1;
        if (firstFilterOperator === 'IN_THIS_TIME_UNIT' && secondFilterOperator === 'IN_LAST_TIME_UNIT' && isFirstTimeUnitComparedToLast) {
          return visualOrder.set('direction', SortOrders.DESCENDING);
        }
      }
    }
    return visualOrder;
  };
  return DatawellVisualization({
    type: DATAWELL,
    data: showPercentChangeComparison ? buildComparisonDataset(report, report.getIn(['visual', 'encodings']), primaryDataset, mapOrder(), fiscalYearMonthName) : {
      primary: primaryDataset
    },
    layer: {
      dataset: 'primary',
      compare: 'compare',
      compact,
      direction: group ? Directions.vertical : Directions.horizontal,
      group: group ? getEncodingColumnAlias(group) : undefined,
      fields: values.map(getEncodingColumnAlias),
      order: mapOrder(),
      groupFormattingOptions: groupEncodingOptions,
      valueFormattingOptions: valueOptionsByColumn
    }
  });
};